/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: split-web
 *
 * Facturación = gestión (plan actual, proveedor, cancelar genérico).
 * Conversión: el CTA va directo al checkout MP (la pantalla Activar queda para
 * entry points fríos: deep link /planes, paywalls de features, modals).
 */
import React, { useEffect, useMemo } from 'react';
import {
  ActivityIndicator,
  Linking,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts';
import { ModalScrollScreen } from '../../shared/layout/ModalScrollScreen';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import {
  branchUsageFromEntitlements,
  userUsageFromEntitlements,
} from '../../../../../shared/plan-benefits';
import {
  APPLE_MANAGE_SUBSCRIPTIONS_URL,
  GOOGLE_PLAY_MANAGE_SUBSCRIPTIONS_URL,
  MERCADO_PAGO_SUBSCRIPTIONS_URL,
} from '../../../../../shared/subscription-routing';
import {
  formatArs,
  subscriptionProviderLabel,
} from '../../../../../shared/billing-catalog';
import {
  creceActivateCopy,
  creceManageCopy,
} from '../../../../../shared/plan-crece-copy';
import { volumeUsageLine } from './softLimitUsage';
import { useBillingWebMercadoPago } from './hooks/useBillingWebMercadoPago';
import { normalizeSubscriptionProvider } from './billingScreen.utils';
import { billingStyles as styles } from './billingScreen.styles';
import { settingsStyles } from '../../settings/screens/settingsScreen.styles';
import { BillingCurrentPlanCard } from './components/BillingCurrentPlanCard';
import { PayerEmailRow } from './components/PayerEmailRow';
import {
  PlanOptionsPreview,
  planOptionsFor,
  recommendedUpgradePlan,
} from './components/PlanOptionsPreview';
import { SignupTrialPromoBanner } from './components/SignupTrialPromoBanner';
import { isBillingEnabled } from './isBillingEnabled';
import { trackCreceFunnel } from './trackCreceFunnel';
import { CRECE_FUNNEL_EVENTS } from '../../../../../shared/crece-funnel';

export const BillingScreen = () => {
  const vm = useBillingWebMercadoPago();
  const { layoutWide } = useContentLayout('settingsWide');

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  if (!isBillingEnabled()) {
    return (
      <ModalScrollScreen
        variant="settingsWide"
        contentContainerStyle={styles.screenGap}
      >
        <View style={settingsStyles.card}>
          <Text style={settingsStyles.cardTitle}>Sin abono</Text>
          <Text style={settingsStyles.caption}>
            Vuelto no cobra abono ni comisión por venta. Está todo, sin límites. El aporte
            voluntario del comprador es opcional y aparte.
          </Text>
        </View>
      </ModalScrollScreen>
    );
  }

  const { refreshing, onRefresh: onPullRefresh } = useScreenLoadShell(
    'Billing',
    async (opts) => { await vm.refresh(opts); }
  );

  const branchUsage = useMemo(() => {
    if (!vm.entitlements) return null;
    return branchUsageFromEntitlements(vm.entitlements);
  }, [vm.entitlements]);

  const userUsage = useMemo(() => {
    if (!vm.entitlements) return null;
    return userUsageFromEntitlements(vm.entitlements);
  }, [vm.entitlements]);

  // Volumen mensual orientativo en Gratis y Emprende; Crece no tiene tope.
  const volumeUsage = useMemo(() => {
    if (!vm.entitlements || !['gratis', 'emprende'].includes(vm.subscribedTier)) return null;
    return volumeUsageLine(vm.entitlements.volume_soft_limit?.metrics);
  }, [vm.entitlements, vm.subscribedTier]);

  const subscriptionProvider = useMemo(
    () => normalizeSubscriptionProvider(vm.entitlements?.subscription_provider),
    [vm.entitlements?.subscription_provider],
  );

  const isEmployee = vm.entitlements !== null && vm.entitlements.is_owner === false;
  const isOwner = vm.entitlements?.is_owner === true;
  const planCode = (vm.entitlements?.plan_code ?? '').trim().toLowerCase();
  const currentPlanKey = planCode === 'legacy' ? 'legacy' : vm.subscribedTier;
  const canUpgrade =
    isOwner &&
    !vm.loading &&
    (vm.subscribedTier === 'gratis' || vm.subscribedTier === 'emprende') &&
    planCode !== 'crece';

  /**
   * Prefetch del checkout pendiente al entrar a Facturación: el plan recomendado
   * (o el primero visible) ya tiene init_point cuando el usuario toca la card,
   * así el click va directo a MP sin pantalla intermedia.
   */
  const prefetchPlan = useMemo(() => {
    if (!canUpgrade) return null;
    return (
      recommendedUpgradePlan(vm.catalog, vm.entitlements, currentPlanKey) ??
      planOptionsFor(currentPlanKey)[0] ??
      null
    );
  }, [canUpgrade, vm.catalog, vm.entitlements, currentPlanKey]);

  useEffect(() => {
    if (prefetchPlan) vm.prefetchMpCheckout(prefetchPlan);
  }, [prefetchPlan, vm.prefetchMpCheckout]);

  const introText = useMemo(() => {
    switch (subscriptionProvider) {
      case 'apple':
        return 'Tu plan se cobra por App Store.';
      case 'google':
        return 'Tu plan se cobra por Google Play.';
      case 'mercadopago':
        return 'Tarjeta, cobros y baja: las manejás en Mercado Pago.';
      default:
        return null;
    }
  }, [subscriptionProvider]);

  const manageLabel = useMemo(() => {
    switch (subscriptionProvider) {
      case 'apple':
        return creceManageCopy.openAppleSubscriptions;
      case 'google':
        return creceManageCopy.openGoogleSubscriptions;
      case 'mercadopago':
        return creceManageCopy.openMpSubscriptions;
      default:
        return null;
    }
  }, [subscriptionProvider]);

  const cancelLabel = useMemo(() => {
    switch (subscriptionProvider) {
      case 'apple':
        return creceManageCopy.cancelApple;
      case 'google':
        return creceManageCopy.cancelGoogle;
      case 'mercadopago':
        return creceManageCopy.cancelMp;
      default:
        return creceManageCopy.cancelCta;
    }
  }, [subscriptionProvider]);

  const priceLine = useMemo(() => {
    if (!['emprende', 'crece'].includes(vm.subscribedTier) || !subscriptionProvider) return null;
    const price = vm.subscribedTier === 'emprende' ? vm.emprendePrice : vm.crecePrice;
    return creceManageCopy.priceProviderLine(
      formatArs(price),
      subscriptionProviderLabel(subscriptionProvider),
    );
  }, [vm.subscribedTier, vm.emprendePrice, vm.crecePrice, subscriptionProvider]);

  const openManage = () => {
    if (subscriptionProvider === 'apple') {
      void Linking.openURL(APPLE_MANAGE_SUBSCRIPTIONS_URL);
    } else if (subscriptionProvider === 'google') {
      void Linking.openURL(GOOGLE_PLAY_MANAGE_SUBSCRIPTIONS_URL);
    } else if (subscriptionProvider === 'mercadopago') {
      void Linking.openURL(MERCADO_PAGO_SUBSCRIPTIONS_URL);
    }
  };

  return (
    <ModalScrollScreen
      variant="settingsWide"
      contentContainerStyle={styles.screenGap}
      refreshing={refreshing}
      onRefresh={() => void onPullRefresh()}
    >
      {introText ? <Text style={styles.introLead}>{introText}</Text> : null}

      {vm.grantBanner ? <SignupTrialPromoBanner grant={vm.activePlanGrant} /> : null}

      {vm.loading ? (
        <ActivityIndicator color={ticketColors.gold} style={{ marginVertical: spacing.lg }} />
      ) : vm.entitlements ? (
        <BillingCurrentPlanCard
          entitlements={vm.entitlements}
          displayName={vm.entitlements.display_name ?? '—'}
          branchUsage={branchUsage}
          userUsage={userUsage}
          volumeUsage={volumeUsage}
          isEmployee={isEmployee}
          layoutWide={layoutWide}
          activeGrant={vm.access.mode === 'trial' ? vm.activePlanGrant : null}
          priceLine={priceLine}
        />
      ) : (
        <View style={settingsStyles.card}>
          <Text style={settingsStyles.cardTitle}>Plan actual</Text>
          <Text style={settingsStyles.caption}>No pudimos cargar el plan. Reintentá más tarde.</Text>
        </View>
      )}

      {canUpgrade ? (
        <View style={styles.planCardsBlock}>
          <PlanOptionsPreview
            catalog={vm.catalog}
            entitlements={vm.entitlements}
            currentPlanCode={currentPlanKey}
            onSelect={(plan) => {
              trackCreceFunnel(CRECE_FUNNEL_EVENTS.payRedirect, {
                channel: 'mercadopago',
                plan_code: plan,
              });
              void vm.startMpCheckout(plan);
            }}
            onPrefetch={(plan) => vm.prefetchMpCheckout(plan)}
            busyPlan={vm.subscribing}
            ctaLabel="Continuar con Mercado Pago"
            trustLine={creceActivateCopy.mpTrustLine}
          />
          {vm.subError ? <Text style={styles.errorText}>{vm.subError}</Text> : null}
          {vm.mpBanner ? <Text style={[settingsStyles.caption, styles.finePrint]}>{vm.mpBanner}</Text> : null}
          <Text style={styles.finePrint}>{creceActivateCopy.perOrgLine}</Text>
          <PayerEmailRow email={vm.payerEmail} defaultEmail={vm.defaultPayerEmail} onChange={vm.setPayerEmail} />
        </View>
      ) : null}

      {manageLabel && isOwner && vm.subscribedTier !== 'gratis' ? (
        <>
          <TouchableOpacity
            style={styles.ctaSecondary}
            onPress={openManage}
            accessibilityRole="button"
          >
            <Text style={styles.ctaSecondaryText}>{manageLabel}</Text>
          </TouchableOpacity>
          <View style={settingsStyles.card}>
            <Text style={settingsStyles.cardTitle}>{creceManageCopy.cancelTitle}</Text>
            <Text style={settingsStyles.caption}>{creceManageCopy.cancelBody}</Text>
          </View>
          <TouchableOpacity
            style={styles.ctaDangerGhost}
            onPress={openManage}
            accessibilityRole="button"
          >
            <Text style={styles.ctaDangerGhostText}>{cancelLabel}</Text>
          </TouchableOpacity>
        </>
      ) : null}
    </ModalScrollScreen>
  );
};
